import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { 
    collection, 
    query, 
    orderBy, 
    onSnapshot, 
    doc, 
    deleteDoc, 
    updateDoc,
    getDocs,
    writeBatch
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import './Members.css';

const Members = () => {
    const { isAdmin, userData } = useAuth();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [allMembers, setAllMembers] = useState([]);
    const [yearGroups, setYearGroups] = useState(new Map());
    const [currentFilter, setCurrentFilter] = useState(null);
    const [currentFilteredMembers, setCurrentFilteredMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [imageViewer, setImageViewer] = useState({ show: false, url: '' });
    const [showEditYearModal, setShowEditYearModal] = useState(false);
    const [editingYear, setEditingYear] = useState(null);
    const [newYearName, setNewYearName] = useState('');
    
    // Store unsubscribe function
    const unsubscribeRef = useRef(null);
    // Flag to prevent multiple subscriptions
    const isSubscribedRef = useRef(false);

    // Check if user is super admin
    const isSuperAdmin = userData?.isSuperAdmin === true;

    useEffect(() => {
        loadMembers();
        
        // Cleanup on unmount
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
                isSubscribedRef.current = false;
            }
        };
    }, []);

    const loadMembers = () => {
        // Unsubscribe from previous listener if exists
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
            isSubscribedRef.current = false;
        }

        // Prevent duplicate subscriptions
        if (isSubscribedRef.current) {
            return;
        }

        const membersQuery = query(collection(db, 'users'), orderBy('createdAt', 'asc'));
        
        const unsubscribe = onSnapshot(membersQuery, (snapshot) => {
            const members = [];
            const groups = new Map();

            snapshot.forEach(doc => {
                const user = doc.data();
                const userId = doc.id;
                const yearName = user.yearName || 'Not specified';
                const yearGroupPrefix = getYearGroupPrefix(yearName);

                const memberData = {
                    id: userId,
                    name: user.fullName || user.displayName || 'Member',
                    phone: user.phone || 'N/A',
                    email: user.email || '',
                    year: yearName,
                    yearGroupPrefix: yearGroupPrefix,
                    dateJoined: user.dateJoined || user.joinDate || null,
                    createdAt: user.createdAt || null,
                    status: user.status || 'inactive',
                    photoURL: user.photoURL || null,
                    initials: (user.fullName || user.displayName || 'M').charAt(0).toUpperCase()
                };

                members.push(memberData);

                if (!groups.has(yearGroupPrefix)) {
                    groups.set(yearGroupPrefix, {
                        displayName: yearName,
                        members: [],
                        count: 0,
                        prefix: yearGroupPrefix
                    });
                }

                groups.get(yearGroupPrefix).members.push(memberData);
                groups.get(yearGroupPrefix).count++;
            });

            setAllMembers(members);
            setYearGroups(groups);
            setLoading(false);
        }, (error) => {
            console.error('Error loading members:', error);
            showToast('Error loading members', 'error');
            setLoading(false);
        });

        // Store unsubscribe function
        unsubscribeRef.current = unsubscribe;
        isSubscribedRef.current = true;
    };

    const getYearGroupPrefix = (yearName) => {
        if (!yearName || yearName.trim() === '') return 'unk';
        const prefix = yearName.substring(0, 3).toLowerCase();
        return prefix === '' ? 'unk' : prefix;
    };

    const formatDate = (date) => {
        if (!date) return '';
        try {
            const dateObj = date.toDate ? date.toDate() : new Date(date);
            return dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return 'Invalid date';
        }
    };

    const getStatusClass = (status) => {
        status = status.toLowerCase();
        if (status.includes('active')) return 'members-status-active';
        if (status.includes('associate')) return 'members-status-associate';
        return 'members-status-inactive';
    };

    const handleFilterClick = (prefix) => {
        if (currentFilter === prefix) {
            setCurrentFilter(null);
            setCurrentFilteredMembers([]);
        } else {
            setCurrentFilter(prefix);
            const group = yearGroups.get(prefix);
            setCurrentFilteredMembers(group?.members || []);
        }
    };

    const handleBackToGroups = () => {
        setCurrentFilter(null);
        setCurrentFilteredMembers([]);
        setSearchTerm('');
    };

    const handleDeleteMember = async (userId) => {
        if (!isAdmin) {
            showToast('Only administrators can delete members', 'error');
            return;
        }
        
        const result = await Swal.fire({
            title: 'Delete Member?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'users', userId));
                showToast('Member deleted successfully!', 'success');
            } catch (error) {
                console.error('Error deleting member:', error);
                showToast('Failed to delete member', 'error');
            }
        }
    };

    // WhatsApp function
    const handleWhatsApp = (phone) => {
        if (!phone || phone === 'N/A') {
            showToast('No phone number available for this member', 'error');
            return;
        }

        const formattedPhone = phone.replace(/\D/g, '');
        
        let whatsappNumber = formattedPhone;
        if (formattedPhone.startsWith('0')) {
            whatsappNumber = '254' + formattedPhone.substring(1);
        } else if (!formattedPhone.startsWith('254')) {
            whatsappNumber = '254' + formattedPhone;
        }

        const whatsappUrl = `https://wa.me/${whatsappNumber}`;
        window.open(whatsappUrl, '_blank');
    };

    const handleImageClick = (url) => {
        if (url) {
            setImageViewer({ show: true, url });
        }
    };

    // Open edit year modal
    const handleEditYear = (group) => {
        setEditingYear(group);
        setNewYearName(group.displayName);
        setShowEditYearModal(true);
    };

    // Save year group rename
    const handleSaveYearRename = async () => {
        if (!newYearName.trim()) {
            showToast('Please enter a valid year name', 'error');
            return;
        }

        if (newYearName.trim() === editingYear.displayName) {
            showToast('No changes to save', 'info');
            setShowEditYearModal(false);
            return;
        }

        // Confirm action
        const result = await Swal.fire({
            title: 'Rename Year Group?',
            text: `This will update ${editingYear.count} member(s) from "${editingYear.displayName}" to "${newYearName.trim()}". This action cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1034A6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, rename all!',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setLoading(true);

        try {
            // Get all users who have this year group
            const usersRef = collection(db, 'users');
            const snapshot = await getDocs(usersRef);
            
            const batch = writeBatch(db);
            let updatedCount = 0;

            const updates = [];
            snapshot.docs.forEach((document) => {
    const userData = document.data();
    if (userData.yearName === editingYear.displayName) {
        const userRef = doc(db, 'users', document.id);
                    updates.push({
                        ref: userRef,
                        data: {
                            yearName: newYearName.trim(),
                            updatedAt: new Date()
                        }
                    });
                    updatedCount++;
                }
            });

            // Execute all updates in batch
            updates.forEach(({ ref, data }) => {
                batch.update(ref, data);
            });

            await batch.commit();

            showToast(`Successfully renamed "${editingYear.displayName}" to "${newYearName.trim()}" (${updatedCount} members updated)`, 'success');
            setShowEditYearModal(false);
            setEditingYear(null);
            setNewYearName('');
            
            // Reload members - reset subscription and reload
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
                isSubscribedRef.current = false;
            }
            // Small delay to ensure cleanup
            setTimeout(() => {
                loadMembers();
            }, 100);
            
        } catch (error) {
            console.error('Error renaming year group:', error);
            showToast('Failed to rename year group: ' + (error.message || 'Unknown error'), 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete year group (merge members to "Not specified")
    const handleDeleteYearGroup = async (group) => {
        if (group.displayName === 'Not specified') {
            showToast('Cannot delete "Not specified" group', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete Year Group?',
            text: `This will move all ${group.count} member(s) from "${group.displayName}" to "Not specified". This action cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete and move!',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setLoading(true);

        try {
            const usersRef = collection(db, 'users');
            const snapshot = await getDocs(usersRef);
            
            const batch = writeBatch(db);
            let updatedCount = 0;

           snapshot.docs.forEach((document) => {
    const userData = document.data();
    if (userData.yearName === group.displayName) {
        const userRef = doc(db, 'users', document.id);
                    batch.update(userRef, {
                        yearName: 'Not specified',
                        updatedAt: new Date()
                    });
                    updatedCount++;
                }
            });

            await batch.commit();

            showToast(`Successfully moved ${updatedCount} members from "${group.displayName}" to "Not specified"`, 'success');
            
            // Reload members
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
                isSubscribedRef.current = false;
            }
            setTimeout(() => {
                loadMembers();
            }, 100);
            
        } catch (error) {
            console.error('Error deleting year group:', error);
            showToast('Failed to delete year group: ' + (error.message || 'Unknown error'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const filteredMembers = searchTerm
        ? currentFilteredMembers.filter(member =>
            member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.year.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : currentFilteredMembers;

    if (loading) {
        return (
            <div className="text-center" style={{ padding: '50px' }}>
                <i className="fas fa-spinner fa-spin fa-3x" style={{ color: '#1034A6' }}></i>
                <p style={{ marginTop: '20px' }}>Loading members...</p>
            </div>
        );
    }

    return (
        <div className="members-page">
            <div className="members-page-title">
                <h1>Ministry Members</h1>
                {isSuperAdmin && (
                    <div className="members-admin-actions">
                        <button 
                            className="members-admin-btn"
                            onClick={() => {
                                showToast('Add new year group feature coming soon', 'info');
                            }}
                        >
                            <i className="fas fa-plus-circle"></i> Add Year Group
                        </button>
                    </div>
                )}
            </div>

            {/* Year Group Filters */}
            <div className="members-section-container">
                <div className="members-section-header">
                    <h2>
                        <i className="fas fa-layer-group"></i>
                        Year Groups
                        <span className="members-count-badge">{yearGroups.size} groups</span>
                    </h2>
                </div>
                <div className="members-section-content">
                    <div className="members-filter-grid">
                        {Array.from(yearGroups.entries()).map(([prefix, group]) => (
                            <div
                                key={prefix}
                                className={`members-filter-card ${currentFilter === prefix ? 'active' : ''}`}
                            >
                                <div 
                                    className="members-filter-name"
                                    onClick={() => handleFilterClick(prefix)}
                                >
                                    {group.displayName}
                                </div>
                                <div className="members-filter-details">
                                    <div className="members-filter-prefix">Prefix: {prefix}</div>
                                    <div className="members-filter-count">{group.count}</div>
                                </div>
                                {isSuperAdmin && group.displayName !== 'Not specified' && (
                                    <div className="members-filter-actions">
                                        <button 
                                            className="members-filter-edit"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditYear(group);
                                            }}
                                            title="Edit year group"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button 
                                            className="members-filter-delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteYearGroup(group);
                                            }}
                                            title="Delete year group"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Filtered Members */}
            {currentFilter && (
                <div className="members-section-container">
                    <div className="members-section-header">
                        <h2>
                            <i className="fas fa-users"></i>
                            Filtered Members
                        </h2>
                    </div>
                    <div className="members-section-content">
                        <div className="members-back-to-groups">
                            <button className="members-back-btn" onClick={handleBackToGroups}>
                                <i className="fas fa-arrow-left"></i> Back to All Groups
                            </button>
                        </div>

                        <div className="members-current-filter">
                            <span>
                                <i className="fas fa-filter"></i>
                                Showing members from: <strong>{yearGroups.get(currentFilter)?.displayName}</strong>
                            </span>
                            <span className="members-filter-count">{currentFilteredMembers.length}</span>
                        </div>

                        <div className="members-search-container">
                            <input
                                type="text"
                                placeholder="Search members by name, phone, or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <table className="members-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Name</th>
                                    <th>Phone</th>
                                    <th>Email</th>
                                    <th>Year Group</th>
                                    <th>Date Joined</th>
                                    <th>Status</th>
                                    {isAdmin && <th>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMembers.length === 0 ? (
                                    <tr>
                                        <td colSpan={isAdmin ? 8 : 7} className="members-empty-state">
                                            <i className="fas fa-search"></i>
                                            <h3>No Members Found</h3>
                                            <p>Try adjusting your search criteria</p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredMembers.map(member => (
                                        <tr key={member.id}>
                                            <td>
                                                {member.photoURL ? (
                                                    <img
                                                        src={member.photoURL}
                                                        alt={member.name}
                                                        className="members-profile-pic"
                                                        onClick={() => handleImageClick(member.photoURL)}
                                                    />
                                                ) : (
                                                    <div className="members-profile-pic-placeholder">
                                                        {member.initials}
                                                    </div>
                                                )}
                                            </td>
                                            <td><strong>{member.name}</strong></td>
                                            <td>
                                                <span className="members-phone-number">{member.phone}</span>
                                                <button
                                                    className="members-whatsapp-btn"
                                                    onClick={() => handleWhatsApp(member.phone)}
                                                    disabled={!member.phone || member.phone === 'N/A'}
                                                    title="Send WhatsApp message"
                                                >
                                                    <i className="fab fa-whatsapp"></i>
                                                </button>
                                            </td>
                                            <td>{member.email}</td>
                                            <td>{member.year}</td>
                                            <td>{formatDate(member.dateJoined)}</td>
                                            <td>
                                                <span className={`members-status-badge ${getStatusClass(member.status)}`}>
                                                    {member.status}
                                                </span>
                                            </td>
                                            {isAdmin && (
                                                <td>
                                                    <button
                                                        className="members-action-btn delete"
                                                        onClick={() => handleDeleteMember(member.id)}
                                                        title="Delete member"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Edit Year Modal */}
            {showEditYearModal && editingYear && (
                <div className="members-modal" onClick={() => setShowEditYearModal(false)}>
                    <div className="members-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="members-modal-header">
                            <h3>Edit Year Group</h3>
                            <button
                                className="members-modal-close"
                                onClick={() => setShowEditYearModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="members-modal-body">
                            <p><strong>Current Name:</strong> {editingYear.displayName}</p>
                            <p><strong>Members:</strong> {editingYear.count}</p>
                            
                            <div className="members-form-group">
                                <label htmlFor="newYearName">New Year Name</label>
                                <input
                                    type="text"
                                    id="newYearName"
                                    className="members-form-control"
                                    value={newYearName}
                                    onChange={(e) => setNewYearName(e.target.value)}
                                    placeholder="Enter new year name"
                                />
                            </div>

                            <div className="members-form-note">
                                <i className="fas fa-info-circle"></i>
                                This will update <strong>{editingYear.count}</strong> member{editingYear.count !== 1 ? 's' : ''} from 
                                "{editingYear.displayName}" to "{newYearName || 'New Name'}"
                            </div>
                        </div>
                        <div className="members-modal-footer">
                            <button
                                className="members-btn-secondary"
                                onClick={() => setShowEditYearModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="members-btn-primary"
                                onClick={handleSaveYearRename}
                                disabled={!newYearName.trim() || newYearName.trim() === editingYear.displayName}
                            >
                                <i className="fas fa-save"></i> Rename & Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Viewer Modal */}
            {imageViewer.show && (
                <div className="members-image-viewer" onClick={() => setImageViewer({ show: false, url: '' })}>
                    <img src={imageViewer.url} alt="Profile" />
                </div>
            )}
        </div>
    );
};

export default Members;
